import { Column, DataTable } from '@creditornot/cb-data-table';
import { MessageBubbleTextFilled } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { Tag } from '@creditornot/cb-tag';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { PurchaseOrder } from 'apis/merchant-gateway/merchant-analytics/types';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { RatingSmiley } from 'modules/insights-v2/components/ratings/Smiley';

import InfiniteScrollHelper from './InfiniteScrollHelper';
import {
  DELIVERY_PROVIDER_TYPES,
  DELIVERY_STATUSES_V2,
  DELIVERY_STATUS_TAG_VARIANTS_V2,
  DELIVERY_TYPES_V2,
  RATING_TEXTS,
} from './constants';

const Root = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  border-block-start: 1px solid ${cssThemedValue('borderPrimary')};
  margin-block-start: 12px;
  overflow: auto;
`;

const StyledDataTable: typeof DataTable = styled(DataTable)`
  display: flex;
  flex-direction: column;
  min-width: 780px;
`;

const DeliveryType = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  gap: 0 8px;
`;

const Review = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RatingIconWrapper = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`;
const RatingText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const CommentIcon = styled(MessageBubbleTextFilled)`
  fill: ${cssThemedValue('textPrimary')};
  margin-inline-start: auto;
  height: 16px;
  width: 16px;
  flex-shrink: 0;
`;

type PurchaseOrdersTableProps = {
  className?: string;
  isLoading?: boolean;
  fetchingNextPage: boolean;
  fetchingPreviousPage: boolean;
  onClickPurchase: (purchaseId: string) => void;
  onFetchNextPage?: () => void;
  onFetchPreviousPage?: () => void;
  currency: Currency;
  purchases: PurchaseOrder[];
  timezone: string;
  showFees: boolean;
};

enum ColumnId {
  ORDER_TIME = 'ORDER_TIME',
  ORDER_NUMBER = 'ORDER_NUMBER',
  CUSTOMER = 'CUSTOMER',
  DELIVERY_TYPE = 'DELIVERY_TYPE',
  STATUS = 'STATUS',
  TOTAL = 'TOTAL',
  FEES = 'FEES',
  RATING = 'RATING',
}

const PurchaseOrdersTable = ({
  className,
  currency,
  purchases,
  isLoading,
  fetchingNextPage,
  fetchingPreviousPage,
  onClickPurchase,
  onFetchNextPage,
  onFetchPreviousPage,
  timezone,
  showFees,
}: PurchaseOrdersTableProps) => {
  const { formatMessage, locale } = useIntl();

  const columns: Column<PurchaseOrder, Record<string, unknown>>[] = useMemo(
    () => [
      {
        id: ColumnId.ORDER_TIME,
        name: formatMessage({ id: 'purchases.order-placed' }),
        render: ({ record }) =>
          DateTime.fromISO(record.timeReceivedLocal, { zone: timezone }).toLocaleString(DateTime.DATETIME_SHORT, {
            locale,
          }),
      },

      {
        id: ColumnId.ORDER_NUMBER,
        name: formatMessage({ id: 'purchases.columns.order-number' }),
        render: ({ record }) => `#${record.orderNumber}`,
      },
      {
        id: ColumnId.CUSTOMER,
        name: formatMessage({ id: 'purchases.columns.customer' }),
        render: ({ record }) => record.consumerName,
      },
      {
        id: ColumnId.DELIVERY_TYPE,
        name: formatMessage({ id: 'purchases.delivery-type' }),
        render: ({ record }) => {
          const deliveryTypeMessageId =
            (record.deliveryProvider && DELIVERY_PROVIDER_TYPES[record.deliveryProvider]) ??
            DELIVERY_TYPES_V2[record.deliveryMethod];

          return (
            <DeliveryType>
              <span>
                {deliveryTypeMessageId ? <FormattedMessage id={deliveryTypeMessageId} /> : record.deliveryMethod}
              </span>
              {record.orderType === 'PREORDER' && (
                <Tag variant="outlineBlue">
                  <FormattedMessage id="purchases.preorder" />
                </Tag>
              )}
            </DeliveryType>
          );
        },
      },
      {
        id: ColumnId.STATUS,
        name: formatMessage({ id: 'purchases.status' }),
        render: ({ record }) => (
          <Tag variant={DELIVERY_STATUS_TAG_VARIANTS_V2[record.status] ?? 'lightBlack'}>
            {record.status in DELIVERY_STATUSES_V2 ? (
              <FormattedMessage id={DELIVERY_STATUSES_V2[record.status]} />
            ) : (
              record.status
            )}
          </Tag>
        ),
      },
      {
        id: ColumnId.TOTAL,
        name: formatMessage({ id: 'purchases.sales.total' }),
        render: ({ record }) => <MonetaryAmount amount={record.pricing.finalPrice.gross} currency={currency} />,
      },
      ...(showFees
        ? [
            {
              id: ColumnId.FEES,
              name: formatMessage({ id: 'purchase-history.fees' }),
              render: ({ record }: { record: PurchaseOrder }) => (
                <MonetaryAmount amount={record.pricing.feesFinal.gross ?? 0} currency={currency} />
              ),
            },
          ]
        : []),
      {
        id: ColumnId.RATING,
        name: formatMessage({ id: 'purchases.rating' }),
        render: ({ record }) => (
          <Review>
            {record.review ? (
              <>
                {!!record.review.rating && (
                  <>
                    <RatingIconWrapper>
                      <RatingSmiley rating={record.review.rating} variant="colored" />
                    </RatingIconWrapper>
                    <RatingText>
                      {record.review.rating in RATING_TEXTS ? (
                        <FormattedMessage id={RATING_TEXTS[record.review.rating]} />
                      ) : (
                        record.review.rating
                      )}
                    </RatingText>
                  </>
                )}
                {record.review.hasWrittenReview && <CommentIcon />}
              </>
            ) : (
              <>-</>
            )}
          </Review>
        ),
      },
    ],
    [currency, formatMessage, locale, timezone, showFees],
  );

  const disableFetchMore = isLoading || fetchingPreviousPage || fetchingNextPage;

  return (
    <Root className={className}>
      {/* Use a custom helper for infinite scroll to be able to use design system table for styles */}
      {!disableFetchMore && !!onFetchPreviousPage && <InfiniteScrollHelper onFetchMore={onFetchPreviousPage} />}
      <StyledDataTable<PurchaseOrder>
        horizontalPadding="wide"
        loading={isLoading}
        emptyMessage={{ title: formatMessage({ id: 'purchases.messages.no-purchases' }) }}
        dataSource={purchases}
        getRowKey={record => record.purchaseId}
        onRowClick={rowKey => onClickPurchase(rowKey)}
        columns={columns}
      />
      {!disableFetchMore && !!onFetchNextPage && <InfiniteScrollHelper onFetchMore={onFetchNextPage} />}
    </Root>
  );
};

export default PurchaseOrdersTable;
